import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dateToTime } from 'app/utils/utils';
import {
  BaseRelease,
  ExportReason,
  LabelRelease,
  LabelV2Release,
  Release,
  ReleaseExportConnex,
  ReleaseExportStatus,
  ReleaseType,
  SoundCloudRelease,
  TuneCoreRelease,
} from 'types/releases';
import { Time } from 'types/general.types';
import { SoundCloudVersionDetailsFormData } from 'app/components/Releases/SoundCloudVersionDetailsForm/types';
import {
  attachAudioToReleaseEffect,
  createReleaseEffect,
  createReleaseVersionEffect,
  deleteReleaseEffect,
  deleteReleaseSongEffect,
  deleteReleaseVersionEffect,
  exportConnexReleaseEffect,
  exportTcReleaseEffect,
  fetchAudioEffect,
  fetchCreateReleaseValidationsEffect,
  fetchExportReleaseValidationsEffect,
  fetchReleaseEffect,
  fetchReleaseVersionEffect,
  fetchReleaseVersionsDetailsEffect,
  fetchSoundCloudPlaylistTypesDDLEffect,
  fetchUpdateReleaseSongTitleValidationsEffect,
  fetchUpdateReleaseValidationsEffect,
  fetchUpdateReleaseVersionValidationsEffect,
  fetchUpdateSCVersionAudioValidationsEffect,
  fetchUpdateVersionSongValidationsEffect,
  fetchVersionTypesDDLEffect,
  getReleasesListEffect,
  handleReplaceAudioResetEffect,
  replaceVersionAudioEffect,
  syncVersionEffect,
  updateLabelVersionEffect,
  updateReleaseEffect,
  updateReleaseSongTitleEffect,
  updateReleaseVersionEffect,
  updateReleaseVersionMetadataEffect,
  updateVersionCreditsEffect,
} from './effects';
import { ReleasesState, syncSoundCloudVersionList } from './slice.utils';
import { getPopulatedVersion } from './utils';

export const releasesInitialState: ReleasesState = {
  releases: [],
  syncStatus: {},
  currentlyReplacingAudio: {},
  playlistTypesList: [],
};

const setReleaseExportFn = (
  state,
  { payload }: PayloadAction<ReleaseExportConnex | undefined>
) => {
  state.export = payload
    ? {
        releaseGuid: payload.guid,
        isAudioUploadSupported: payload.isAudioUploadSupported,
      }
    : undefined;
};

const slice = createSlice({
  name: 'releases',
  initialState: releasesInitialState,
  reducers: {
    handleReplaceAudioUpdate(
      state,
      {
        payload,
      }: PayloadAction<{
        originalAudioGuid: string;
        primaryAudioGuid: string;
        versionGuid: string;
      }>
    ) {
      const { originalAudioGuid, primaryAudioGuid, versionGuid } = payload;
      if (state.currentlyReplacingAudio[versionGuid]) {
        delete state.currentlyReplacingAudio[versionGuid];
      }
      if (state.releaseVersion?.metadata.guid === versionGuid) {
        const replacedAudioOriginalGuid =
          state.releaseVersion.metadata.originalAudioGuid;
        if (state.release?.originalAudioGuids) {
          state.release.originalAudioGuids = state.release.originalAudioGuids.filter(
            (guid) => guid !== replacedAudioOriginalGuid
          );
          state.release.originalAudioGuids.push(originalAudioGuid);
        }

        state.releaseVersion.metadata.originalAudioGuid = originalAudioGuid;
        state.releaseVersion.metadata.primaryAudioGuid = primaryAudioGuid;
      }
      syncSoundCloudVersionList(
        state,
        {
          primaryAudioGuid,
          isNew: false,
        },
        versionGuid
      );
    },
    updateReleaseStatus(state, { payload }: PayloadAction<ExportReason>) {
      if (state.release) {
        (state.release as
          | SoundCloudRelease
          | LabelV2Release).isReadyForExportReason = payload;
      }
    },
    updateLatestExportAction(
      state,
      {
        payload,
      }: PayloadAction<{
        export: BaseRelease['latestExportShipment'];
        date: Time;
      }>
    ) {
      if (!payload.export) {
        return;
      }
      const { parentGuid, status } = payload.export;

      if (parentGuid && state.release?.guid === parentGuid) {
        (state.release as TuneCoreRelease).latestExport = {
          ...(state.release as TuneCoreRelease)?.latestExport,
          status,
        };
      }
      state.releases = state.releases.map((release) => {
        if (release.guid === parentGuid) {
          return {
            ...release,
            ...(release.type === ReleaseType.TUNECORE
              ? {
                  latestExport: {
                    updated: payload.date,
                    created: payload.date,
                    ...(state.release as TuneCoreRelease)?.latestExport,
                    status,
                  },
                }
              : {
                  latestExportShipment: {
                    ...release.latestExportShipment,
                    ...payload.export,
                  },
                }),
          } as Release;
        }

        return release;
      });
    },
    setTuneCoreSuccessDialog(
      state,
      { payload }: PayloadAction<ReleasesState['tuneCoreSuccessDialog']>
    ) {
      state.tuneCoreSuccessDialog = payload;
    },
    setTuneCoreFailureDialog(
      state,
      { payload }: PayloadAction<ReleasesState['tuneCoreFailureDialog']>
    ) {
      state.tuneCoreFailureDialog = payload;
    },
    setReleaseExport: setReleaseExportFn,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createReleaseEffect.fulfilled, (state, { payload }) => {
        state.release = payload;
        state.releases.push(payload);
      })
      .addCase(
        fetchCreateReleaseValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.createReleaseValidations = payload;
        }
      )
      .addCase(fetchReleaseEffect.fulfilled, (state, { payload }) => {
        state.release = payload;
      })
      .addCase(
        updateReleaseVersionEffect.fulfilled,
        (state, { meta: { arg } }) => {
          const formData = arg.formData as SoundCloudVersionDetailsFormData;
          if (state.releaseVersion) {
            state.releaseVersion.metadata = {
              ...state.releaseVersion.metadata,
              ...formData,
            };
          }
          if (
            state.releaseVersion?.lyrics &&
            formData.excludeLyrics !== undefined
          ) {
            state.releaseVersion.lyrics.isExcluded = formData.excludeLyrics;
          }
          syncSoundCloudVersionList(state, {
            ...(formData.versionTitle ? { title: formData.versionTitle } : {}),
            updated: dateToTime(new Date()),
          });
        }
      )
      .addCase(getReleasesListEffect.pending, (state) => {
        state.release = undefined;
      })
      .addCase(
        getReleasesListEffect.fulfilled,
        (state, { payload: { items, _links }, meta }) => {
          const isNext = Boolean(meta.arg.next);
          state._releasesListLinks = _links;
          if (isNext) {
            state.releases.push(...items);
            return;
          }
          state.releases = items;
        }
      )
      .addCase(updateReleaseSongTitleEffect.fulfilled, (state, { payload }) => {
        state.release = payload;
      })
      .addCase(
        fetchUpdateReleaseSongTitleValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.updateReleaseSongTitleValidations = payload;
        }
      )
      .addCase(
        fetchUpdateVersionSongValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.updateVersionSongValidations = payload;
        }
      )
      .addCase(
        fetchUpdateReleaseVersionValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.updateReleaseVersionValidations = payload;
        }
      )
      .addCase(
        fetchUpdateSCVersionAudioValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.updateSCVersionAudioValidations = payload;
        }
      )
      .addCase(
        fetchUpdateReleaseValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.updateReleaseValidations = payload;
        }
      )
      .addCase(updateReleaseEffect.fulfilled, (state, { payload }) => {
        const latestExport = (state?.release as TuneCoreRelease)?.latestExport;
        const versions = (state?.release as LabelV2Release)?.versions;
        const isInvalid = (state?.release as LabelV2Release)?.isInvalid;

        state.release = {
          latestExport,
          isInvalid,
          ...payload,
          versions,
        };
      })
      .addCase(
        updateLabelVersionEffect.fulfilled,
        (state, { meta, payload }) => {
          const { formData } = meta.arg;
          if (state.releaseVersion) {
            state.releaseVersion.metadata.versionTitle = formData.versionTitle;
            state.releaseVersion.song.updated = payload.updated;
          }
        }
      )
      .addCase(
        fetchExportReleaseValidationsEffect.fulfilled,
        (state, { payload }) => {
          state.exportReleaseValidations = payload;
        }
      )
      .addCase(
        fetchReleaseVersionEffect.fulfilled,
        (state, { payload, meta }) => {
          if (!meta.arg.validation) {
            state.releaseVersion = payload;
          }
          if (state.release?.type === ReleaseType.SOUNDCLOUD) {
            syncSoundCloudVersionList(
              state,
              {
                title: payload.metadata.versionTitle,
                updated: payload.metadata.updated,
              },
              payload.metadata.guid
            );
          }
          if (state.release?.type === ReleaseType.LABEL_V2) {
            const release = state.release as LabelV2Release;
            release.isInvalid =
              release.isInvalid || (payload.validationErrors || []).length > 0;

            const version = release.versions.find(
              ({ guid }) => payload.metadata.guid === guid
            );
            if (version) {
              version.validationErrors = payload.validationErrors ?? [];
              version.contributions = payload.contributions;
            }
          }
        }
      )
      .addCase(fetchVersionTypesDDLEffect.fulfilled, (state, { payload }) => {
        state.releaseVersionTypes = payload;
      })
      .addCase(syncVersionEffect.pending, (state, { meta }) => {
        state.syncStatus[meta.arg.versionGuid] = true;
      })
      .addCase(syncVersionEffect.fulfilled, (state, { meta, payload }) => {
        state.syncStatus[meta.arg.versionGuid] = false;
        state.releaseVersion = payload;
      })
      .addCase(syncVersionEffect.rejected, (state, { meta }) => {
        state.syncStatus[meta.arg.versionGuid] = false;
      })
      .addCase(createReleaseVersionEffect.fulfilled, (state, { payload }) => {
        state.release = payload;
      })
      .addCase(updateVersionCreditsEffect.fulfilled, (state, { payload }) => {
        const contributions = state.releaseVersion?.contributions;
        if (contributions) {
          const contributionIndex = contributions.findIndex(
            (contribution) =>
              contribution.contributionGuid === payload.contributionGuid
          );
          contributions[contributionIndex] = payload;
        }
      })
      .addCase(
        updateReleaseVersionMetadataEffect.fulfilled,
        (state, { payload }) => {
          const { guid: versionGuid, type, updated, songGuid } = payload;

          if (!state.release) {
            return;
          }
          const song = (state.release as LabelRelease).songs.find(
            (s) => s.songGuid === songGuid
          );
          if (song) {
            const songVersion = song.versions.find(
              (version) => version.guid === versionGuid
            );
            if (songVersion) {
              songVersion.type = type;
              songVersion.updated = updated;
            }
          }
        }
      )
      .addCase(deleteReleaseVersionEffect.fulfilled, (state, { payload }) => {
        const latestExport = (state?.release as TuneCoreRelease)?.latestExport;

        state.release = {
          latestExport,
          ...payload,
        };
      })
      .addCase(deleteReleaseSongEffect.fulfilled, (state, { payload }) => {
        state.release = payload;
      })
      .addCase(deleteReleaseEffect.fulfilled, (state, { meta }) => {
        state.releases = state.releases.filter(
          (release) => release.guid !== meta.arg.releaseGuid
        );
      })
      .addCase(fetchAudioEffect.fulfilled, (state, { payload }) => {
        syncSoundCloudVersionList(
          state,
          {
            primaryAudioGuid: payload.guid,
          },
          payload.parent
        );
      })
      .addCase(
        attachAudioToReleaseEffect.fulfilled,
        (state, { payload, meta }) => {
          const addedVersions = meta.arg.selectedAudioArray.map(
            (audio, index) => getPopulatedVersion(audio, payload[index])
          );
          (state.release as SoundCloudRelease).versions = [
            ...(state.release as SoundCloudRelease).versions,
            ...addedVersions,
          ];
        }
      )
      .addCase(replaceVersionAudioEffect.pending, (state) => {
        const currentVersionGuid = state.releaseVersion?.metadata.guid;
        if (currentVersionGuid) {
          state.currentlyReplacingAudio[currentVersionGuid] = true;
        }
      })
      .addCase(replaceVersionAudioEffect.rejected, (state) => {
        const currentVersionGuid = state.releaseVersion?.metadata.guid;
        if (
          currentVersionGuid &&
          state.currentlyReplacingAudio[currentVersionGuid]
        ) {
          delete state.currentlyReplacingAudio[currentVersionGuid];
        }
      })
      .addCase(handleReplaceAudioResetEffect.fulfilled, (state, { meta }) => {
        const { versionGuid } = meta.arg;
        if (state.currentlyReplacingAudio[versionGuid]) {
          delete state.currentlyReplacingAudio[versionGuid];
          if (state.releaseVersion?.metadata.guid === versionGuid) {
            state.releaseVersion.metadata.originalAudioGuid = undefined;
            state.releaseVersion.metadata.primaryAudioGuid = undefined;
          }
          syncSoundCloudVersionList(
            state,
            {
              primaryAudioGuid: undefined,
            },
            versionGuid
          );
        }
      })
      .addCase(
        fetchSoundCloudPlaylistTypesDDLEffect.fulfilled,
        (state, { payload }) => {
          const { playlistTypes } = payload;
          const listKeys = Object.keys(playlistTypes);

          state.playlistTypesList = listKeys.map((key: string) => ({
            ...playlistTypes[key],
          }));
        }
      )
      .addCase(exportTcReleaseEffect.fulfilled, (state) => {
        if (state.release) {
          (state.release as TuneCoreRelease).latestExport = {
            status: ReleaseExportStatus.PROCESSING,
          };
        }
      })
      .addCase(
        fetchReleaseVersionsDetailsEffect.fulfilled,
        (state, { payload }) => {
          state.releaseVersionsDetails = payload;
        }
      )
      .addCase(exportConnexReleaseEffect.pending, (state) => {
        if ((state.release as LabelV2Release)?.isReadyForExportReason) {
          (state.release as LabelV2Release).isReadyForExportReason =
            'Processing';
        }
      })
      .addCase(exportConnexReleaseEffect.fulfilled, setReleaseExportFn)
      .addCase(exportConnexReleaseEffect.rejected, (state) => {
        if ((state.release as LabelV2Release)?.isReadyForExportReason) {
          (state.release as LabelV2Release).isReadyForExportReason = 'Ready';
        }
      });
  },
});

export const {
  handleReplaceAudioUpdate,
  updateReleaseStatus,
  setTuneCoreSuccessDialog,
  setTuneCoreFailureDialog,
  updateLatestExportAction,
  setReleaseExport,
} = slice.actions;

export const releasesReducer = slice.reducer;
